/**
 * Shared /guides metadata. Lives in the shared package so the Worker
 * dispatcher can build SEO meta tags and sitemaps from the same source
 * of truth as the web app.
 */

export type GuideMeta = {
  /** URL path, e.g. "/guides/how-to-write-prompts". */
  path: string;
  /** H1 of the guide. */
  title: string;
  /** One-sentence summary used in cards and meta descriptions. */
  summary: string;
  /** Short blurb (1-2 sentences) shown in the /guides index card. */
  description: string;
  /** Estimated reading time in minutes. */
  readingMinutes: number;
  /** Visualry category the guide pairs with, if any. */
  category?: { slug: string; name: string };
  /** Category slug used to pick the static hero/thumbnail image. */
  coverSlug: string;
  /** Difficulty bucket for filtering / sorting. */
  level: 'Beginner' | 'Intermediate' | 'Advanced';
  /** Tag string used as a small chip on the card. */
  tag: string;
};

export const GUIDES = [
  {
    path: '/guides/how-to-write-prompts',
    title: 'How to write prompts that generate stunning AI images',
    summary:
      'The fundamentals of writing AI image prompts. The anatomy of a great prompt, plug-and-play templates, and how to fix common results.',
    description:
      'Start here if you are new. Learn the five parts every great prompt has, then copy five ready-made templates you can paste straight into Visualry.',
    readingMinutes: 9,
    level: 'Beginner',
    tag: 'Foundations',
    coverSlug: 'luxury',
  },
  {
    path: '/guides/how-to-use-reference-images',
    title: 'How to use reference images to control your AI output',
    summary:
      'Get consistent, on-brand results by uploading reference images. What works, what does not, and how to combine the Polish subject feature with style references.',
    description:
      "A practical walkthrough of Visualry's reference image upload and the Polish subject toggle — the two features most users underuse.",
    readingMinutes: 7,
    level: 'Beginner',
    tag: 'Feature deep-dive',
    coverSlug: 'social-media',
  },
  {
    path: '/guides/how-to-create-ai-headshots',
    title: 'How to create professional AI headshots',
    summary:
      'Generate LinkedIn, actor, real estate, and corporate headshots with AI. Lighting setups, expressions, backgrounds, attire, and prompt templates for a natural look.',
    description:
      'Step-by-step prompts for corporate, actor, and creative headshots — including the exact lighting, framing, and expression words that make a result look real.',
    readingMinutes: 8,
    level: 'Beginner',
    tag: 'Use case',
    category: { slug: 'headshots', name: 'Headshots' },
    coverSlug: 'headshots',
  },
  {
    path: '/guides/how-to-create-fashion-photography',
    title: 'How to create professional fashion photography with AI',
    summary:
      'Write AI prompts for editorial and commercial fashion photography. Studio lighting, model direction, backdrops, and luxury vs. streetwear aesthetics.',
    description:
      "Editorial vs. commercial, the four classic lighting setups, how to direct a model's pose, and three ready-to-paste fashion prompts for different brand aesthetics.",
    readingMinutes: 10,
    level: 'Intermediate',
    tag: 'Use case',
    category: { slug: 'fashion', name: 'Fashion' },
    coverSlug: 'fashion',
  },
  {
    path: '/guides/how-to-generate-product-photography',
    title: 'How to generate product photography for ecommerce and ads',
    summary:
      'Create ecommerce-ready product images with AI: hero shots, lifestyle scenes, on-model photography, and marketplace-compliant backgrounds.',
    description:
      'The four shot types every product page needs, the angles and lighting that sell, and prompt templates for hero, lifestyle, and on-model shots.',
    readingMinutes: 9,
    level: 'Intermediate',
    tag: 'Use case',
    category: { slug: 'product-photography', name: 'Product Photography' },
    coverSlug: 'product-photography',
  },
  {
    path: '/guides/how-to-create-cinematic-film-posters',
    title: 'How to write prompts for cinematic film posters',
    summary:
      'Compose AI film posters that look like real cinema key art. Lighting, character placement, genre aesthetics, color grading, and copy-paste prompt templates by genre.',
    description:
      'Five genre-specific prompt templates — sci-fi, horror, drama, animation, and thriller — with the lighting and composition words that sell the look.',
    readingMinutes: 8,
    level: 'Intermediate',
    tag: 'Use case',
    category: { slug: 'film-posters', name: 'Film Posters' },
    coverSlug: 'film-posters',
  },
  {
    path: '/guides/how-to-build-a-visual-brand',
    title: 'How to build a consistent visual brand with AI',
    summary:
      'Keep dozens or hundreds of AI images visually consistent for a brand. Keyword anchors, fixed models, mood boards, color palettes, and aspect ratio playbooks.',
    description:
      'A repeatable workflow for agencies and brand owners: define a style anchor, lock the variables, and produce a campaign that looks like one designer made it.',
    readingMinutes: 11,
    level: 'Advanced',
    tag: 'Workflow',
    category: { slug: 'brand-ads', name: 'Brand Ads' },
    coverSlug: 'brand-ads',
  },
] as const satisfies ReadonlyArray<GuideMeta>;

export const GUIDE_PATHS = new Set<string>(GUIDES.map((g) => g.path));
export const GUIDE_SLUGS = GUIDES.map((g) => g.path.replace('/guides/', ''));

export type GuideSlug = (typeof GUIDE_SLUGS)[number];

export const isGuidePath = (pathname: string): boolean => GUIDE_PATHS.has(pathname);
